// src/firebase/wishlist-functions.js

import { doc, setDoc, deleteDoc, collection, query, getDocs, serverTimestamp, getDoc, where } from "firebase/firestore";
import { db, DEFAULT_STORE_ID } from "./firebase";

/**
 * Add a product to the user's wishlist with Firestore fallback
 * @param {object} product - The product to add
 * @param {string} variantId - Optional variant ID (ignored, saved as entire item)
 * @param {object} currentUser - Current user
 * @param {string} language - Current language for localization
 * @returns {Promise<object>} Result with ID of created wishlist item
 */
export const addToWishlist = async (product, variantId = null, currentUser = null, language = 'en') => {
    try {
        // Ensure user is logged in
        if (!currentUser) {
            throw new Error("You must be logged in to add items to your wishlist");
        }

        // Store only essential identifiers - fetch fresh data when needed
        const filteredProductData = {
            // Only store the product ID - everything else will be fetched fresh
            id: product.id || "",
            // Optionally store the product name for quick reference (can be outdated but useful for debugging)
            name: product.name || ""
            // REMOVED: All other product data to ensure fresh data on display
            // This includes: price, inStock, images, variants, descriptions, etc.
        };

        // Create wishlist item with sanitized data - IMPORTANT: We're no longer storing variantId
        const wishlistItem = {
            productId: product.id,
            storeId: product.storeId || DEFAULT_STORE_ID, // Use DEFAULT_STORE_ID if product doesn't have storeId
            // No longer saving variantId, so entire product is saved
            variantId: null,
            dateAdded: serverTimestamp(),
            // Include filtered product data to avoid undefined values
            productData: filteredProductData
        };

        // Generate a unique ID for the wishlist item
        const wishlistItemId = `wish_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

        try {
            // First check if this product is already in the wishlist
            // Modified to only check by productId since we're no longer using variantId
            const existingItem = await isInWishlist(product.id, null, currentUser, true);

            // If it already exists, return success without adding
            if (existingItem && existingItem.id) {
                return {
                    success: true,
                    id: existingItem.id,
                    message: "Item is already in your wishlist",
                    alreadyExists: true
                };
            }

            // Try to save to Firestore
            await setDoc(doc(db, "users", currentUser.uid, "wishlist", wishlistItemId), wishlistItem);

            return {
                success: true,
                id: wishlistItemId,
                message: "Item added to wishlist"
            };
        } catch (firestoreError) {
            console.error("Error adding to Firestore wishlist:", firestoreError);

            // If Firestore fails, save to localStorage as fallback
            try {
                const localWishlist = JSON.parse(localStorage.getItem(`wishlist_${currentUser.uid}`)) || [];

                // Check if product already exists in local wishlist
                // Modified to only check by productId
                const existingItemIndex = localWishlist.findIndex(item =>
                    item.productId === product.id
                );

                if (existingItemIndex >= 0) {
                    return {
                        success: true,
                        id: localWishlist[existingItemIndex].id,
                        message: "Item is already in your wishlist",
                        alreadyExists: true,
                        isLocalStorage: true
                    };
                }

                // For localStorage, convert serverTimestamp to ISO string
                const localWishlistItem = {
                    ...wishlistItem,
                    dateAdded: new Date().toISOString()
                };

                // Add the item with ID to local wishlist
                localWishlist.push({
                    id: wishlistItemId,
                    ...localWishlistItem
                });

                localStorage.setItem(`wishlist_${currentUser.uid}`, JSON.stringify(localWishlist));

                return {
                    success: true,
                    id: wishlistItemId,
                    message: "Item added to wishlist (local storage)",
                    isLocalStorage: true
                };
            } catch (localError) {
                console.error("Error adding to localStorage wishlist:", localError);
                throw new Error("Failed to add item to wishlist");
            }
        }
    } catch (error) {
        console.error("Error in addToWishlist:", error);
        throw error;
    }
};

/**
 * Remove an item from the user's wishlist
 * @param {string} wishlistItemId - ID of the wishlist item to remove
 * @param {object} currentUser - Current user
 * @returns {Promise<object>} Success result
 */
export const removeFromWishlist = async (wishlistItemId, currentUser = null) => {
    try {
        // Ensure user is logged in
        if (!currentUser) {
            throw new Error("You must be logged in to remove items from your wishlist");
        }

        try {
            // Try to remove from Firestore first
            await deleteDoc(doc(db, "users", currentUser.uid, "wishlist", wishlistItemId));

            // Also check localStorage in case it exists there
            try {
                const localWishlist = JSON.parse(localStorage.getItem(`wishlist_${currentUser.uid}`)) || [];
                const updatedWishlist = localWishlist.filter(item => item.id !== wishlistItemId);
                localStorage.setItem(`wishlist_${currentUser.uid}`, JSON.stringify(updatedWishlist));
            } catch (localCheckError) {
                // Ignore errors here, as the primary removal was successful
            }

            return { success: true, message: "Item removed from wishlist" };
        } catch (firestoreError) {
            console.error("Error removing from Firestore wishlist:", firestoreError);

            // If Firestore fails, try localStorage
            try {
                const localWishlist = JSON.parse(localStorage.getItem(`wishlist_${currentUser.uid}`)) || [];
                const updatedWishlist = localWishlist.filter(item => item.id !== wishlistItemId);

                if (updatedWishlist.length !== localWishlist.length) {
                    localStorage.setItem(`wishlist_${currentUser.uid}`, JSON.stringify(updatedWishlist));
                    console.log("Item removed from localStorage wishlist");
                    return {
                        success: true,
                        message: "Item removed from wishlist (local storage)",
                        isLocalStorage: true
                    };
                } else {
                    throw new Error("Item not found in wishlist");
                }
            } catch (localError) {
                console.error("Error removing from localStorage wishlist:", localError);
                throw new Error("Failed to remove item from wishlist");
            }
        }
    } catch (error) {
        console.error("Error in removeFromWishlist:", error);
        throw error;
    }
};

/**
 * Check if a product is in the user's wishlist
 * @param {string} productId - The product ID to check
 * @param {string} variantId - Optional variant ID (no longer used)
 * @param {object} currentUser - Current user
 * @param {boolean} returnItem - Whether to return the item object if found
 * @returns {Promise<boolean|object>} True if the product is in the wishlist, or the item object if returnItem is true
 */
export const isInWishlist = async (productId, variantId = null, currentUser = null, returnItem = false) => {
    try {
        // Ensure user is logged in
        if (!currentUser) {
            return returnItem ? null : false;
        }

        try {
            // Try to check Firestore first
            const wishlistRef = collection(db, "users", currentUser.uid, "wishlist");

            // Only check by product ID, ignoring variantId
            const q = query(wishlistRef, where("productId", "==", productId));

            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const doc = querySnapshot.docs[0];
                return returnItem ? { id: doc.id, ...doc.data() } : true;
            }

            // Not found in Firestore, check localStorage
            return checkLocalWishlist(productId, null, currentUser, returnItem);
        } catch (firestoreError) {
            console.error("Error checking Firestore wishlist:", firestoreError);
            // Fall back to localStorage
            return checkLocalWishlist(productId, null, currentUser, returnItem);
        }
    } catch (error) {
        console.error("Error in isInWishlist:", error);
        return returnItem ? null : false;
    }
};

/**
 * Helper function to check localStorage for wishlist items
 * @param {string} productId - The product ID to check
 * @param {string} variantId - Optional variant ID (no longer used)
 * @param {object} currentUser - Current user
 * @param {boolean} returnItem - Whether to return the item object if found
 * @returns {boolean|object} True if the product is in the local wishlist, or the item object if returnItem is true
 */
const checkLocalWishlist = (productId, variantId = null, currentUser = null, returnItem = false) => {
    try {
        if (!currentUser) return returnItem ? null : false;

        const localWishlist = JSON.parse(localStorage.getItem(`wishlist_${currentUser.uid}`)) || [];

        if (returnItem) {
            const item = localWishlist.find(item => item.productId === productId);
            return item || null;
        }

        return localWishlist.some(item => item.productId === productId);
    } catch (error) {
        console.error("Error checking localStorage wishlist:", error);
        return returnItem ? null : false;
    }
};

/**
 * Synchronize local wishlist with Firestore when connection is restored
 * @param {object} currentUser - Current user
 * @returns {Promise<object>} Sync results
 */
export const syncWishlist = async (currentUser = null) => {
    try {
        // Ensure user is logged in
        if (!currentUser) {
            return { success: false, message: "User not logged in" };
        }

        // Get local wishlist
        const localWishlist = JSON.parse(localStorage.getItem(`wishlist_${currentUser.uid}`)) || [];

        if (localWishlist.length === 0) {
            return { success: true, message: "No local wishlist items to sync", itemsSynced: 0 };
        }

        let syncedCount = 0;
        let errors = [];

        // Try to sync each item to Firestore
        for (const item of localWishlist) {
            try {
                // First check if it already exists in Firestore to avoid duplicates
                // Modified to check only by product ID
                const exists = await checkFirestoreForItem(item.productId, null, currentUser);

                if (!exists) {
                    // Filter out undefined values and prepare the item
                    const sanitizedItem = {
                        productId: item.productId || "",
                        storeId: item.storeId || DEFAULT_STORE_ID, // Use DEFAULT_STORE_ID if missing
                        variantId: null, // No longer using variantId
                        dateAdded: serverTimestamp(),
                        productData: {
                            // Only store essential data - everything else fetched fresh
                            id: item.productData?.id || "",
                            name: item.productData?.name || ""
                            // REMOVED: All other product data to ensure fresh data on display
                        }
                    };

                    await setDoc(doc(db, "users", currentUser.uid, "wishlist", item.id), sanitizedItem);
                    syncedCount++;
                } else {
                    console.log(`Item ${item.id} already exists in Firestore, skipping`);
                    syncedCount++; // Count as synced since it's in Firestore
                }
            } catch (error) {
                console.error(`Error syncing wishlist item ${item.id}:`, error);
                errors.push(item.id);
            }
        }

        // If all items were synced, clear local wishlist
        if (errors.length === 0) {
            localStorage.removeItem(`wishlist_${currentUser.uid}`);
            return {
                success: true,
                message: `Successfully synced ${syncedCount} wishlist items`,
                itemsMigrated: syncedCount
            };
        } else {
            // Otherwise, remove only synced items from local storage
            const remainingItems = localWishlist.filter(item => errors.includes(item.id));
            localStorage.setItem(`wishlist_${currentUser.uid}`, JSON.stringify(remainingItems));

            return {
                success: true,
                message: `Synced ${syncedCount} wishlist items, ${errors.length} failed`,
                itemsMigrated: syncedCount,
                itemsFailed: errors.length
            };
        }
    } catch (error) {
        console.error("Error in syncWishlist:", error);
        return {
            success: false,
            message: "Failed to sync wishlist",
            error: error.message
        };
    }
};

/**
 * Check if an item already exists in Firestore
 * @param {string} productId - Product ID
 * @param {string} variantId - Variant ID (no longer used)
 * @param {object} currentUser - Current user
 * @returns {Promise<boolean>} True if item exists
 */
const checkFirestoreForItem = async (productId, variantId, currentUser) => {
    try {
        const wishlistRef = collection(db, "users", currentUser.uid, "wishlist");

        // Only check by product ID
        const q = query(wishlistRef, where("productId", "==", productId));

        const querySnapshot = await getDocs(q);
        return !querySnapshot.empty;
    } catch (error) {
        console.error("Error checking Firestore for item:", error);
        return false;
    }
};

/**
 * Migration function to fix existing wishlist items with empty storeId
 * @param {object} currentUser - Current user
 * @returns {Promise<object>} Migration results
 */
export const migrateWishlistStoreIds = async (currentUser = null) => {
    try {
        if (!currentUser) {
            return { success: false, message: "User not logged in" };
        }

        // Get all wishlist items for this user
        const wishlistRef = collection(db, "users", currentUser.uid, "wishlist");
        const querySnapshot = await getDocs(wishlistRef);

        let migratedCount = 0;
        let errors = [];

        for (const docSnapshot of querySnapshot.docs) {
            const wishlistItem = docSnapshot.data();

            // Check if storeId is missing or empty
            if (!wishlistItem.storeId || wishlistItem.storeId === "") {
                try {
                    // Update the wishlist item with DEFAULT_STORE_ID
                    const updatedItem = {
                        ...wishlistItem,
                        storeId: DEFAULT_STORE_ID
                    };

                    await setDoc(doc(db, "users", currentUser.uid, "wishlist", docSnapshot.id), updatedItem);
                    migratedCount++;
                    console.log(`Migrated wishlist item ${docSnapshot.id}`);
                } catch (error) {
                    console.error(`Error migrating wishlist item ${docSnapshot.id}:`, error);
                    errors.push(docSnapshot.id);
                }
            }
        }

        return {
            success: true,
            message: `Successfully migrated ${migratedCount} wishlist items`,
            itemsMigrated: migratedCount,
            itemsFailed: errors.length,
            errors
        };
    } catch (error) {
        return {
            success: false,
            message: "Failed to migrate wishlist",
            error: error.message
        };
    }
};